<script setup lang="ts">
import { ref, onBeforeMount, watch, onErrorCaptured, computed } from 'vue'
import { useRoute, type RouteRecordRaw } from 'vue-router'
import routes from '@/router/routes'
import {
  NavBar,
  AppBar,
  AppBreadcrumbs,
  Notifications,
  TopOfPageStickySection,
  ToastNotifications,
  ConfigFlyout
} from '@/components/shared'
import { VApp, VMain } from 'vuetify/components'
import {
  LOGIN_URL,
  LOGIN_URL_NAME,
  RESET_PASSWORD_URL_NAME,
  WORKER_SIGNAL_R,
  SPREADING_JOBS_LIST_URL_NAME,
  HAULAGE_JOBS_LIST_URL_NAME,
  DIGESTATE_JOBS_LIST_URL_NAME,
  SPREADING_JOBS_DETAILS_URL_NAME,
  SPREADING_JOBS_EDIT_JOB_URL_NAME,
  DIGESTATE_JOBS_DETAILS_URL_NAME,
  DIGESTATE_JOBS_EDIT_JOB_URL_NAME,
  HAULAGE_JOBS_DETAILS_URL_NAME,
  HAULAGE_JOBS_EDIT_JOB_URL_NAME
} from '@/utils/constants'
import useGlobalStore from '@/stores/modules/global'
import useAccountStore from '@/stores/modules/account'
import { ErrorDialogBox } from '@/components/shared/dialog-boxes'
import { useNavigator } from '@/hooks'
import useWorkersStore from './stores/modules/workers'
import { logInfo } from './utils/helpers/logger'
import type NotificationType from './enum/NotificationType'
import { MainToWorkerMessageType, WorkerToMainMessageType } from './enum/SignalRWorkerMessageType'
import signalRworker from './workers/SignalRWorker.ts?worker'
import useRealtimeStore from './stores/modules/realtime'

const route = useRoute()
const navigator = useNavigator()
const globalStore = useGlobalStore()
const accountStore = useAccountStore()
const isNavbarCollapsed = ref(false)
const workersStore = useWorkersStore()
const realtimeStore = useRealtimeStore()

const userLoggedInAndNavigated = computed(
  () => globalStore.isUserLoggedIn && route.name !== LOGIN_URL_NAME
)

const signalRWorker = computed(() => workersStore.workers?.[WORKER_SIGNAL_R])

const currentRouteName = computed(() => route.name)

const isUserLoggedIn = !!localStorage.getItem('userId')

const navigateToLogin = () => navigator?.navigateTo(LOGIN_URL)
const validateLoginAndURLValidityAndPerformSetup = async () => {
  if (globalStore.isUserLoggedIn) {
    setupSignalRWorker()
  }
  if (isUserLoggedIn) {
    // await accountStore.getProfile(localStorage.getItem('userId') as string)
    await accountStore.getAccountPermissions()
    // accountStore.getValidationConfig();
  }
  const currentRouteName = route.name

  const extractRouteNames = (routes: RouteRecordRaw[]): string[] => {
    return routes.flatMap((route) => {
      const currentName = typeof route.name === 'string' ? route.name : null

      if (route.children && route.children.length > 0) {
        return [currentName, ...extractRouteNames(route.children)].filter(
          (name): name is string => !!name
        )
      }

      return currentName ? [currentName] : []
    })
  }

  const validRouteNames = extractRouteNames(routes)
  if (!currentRouteName || !validRouteNames.includes(currentRouteName as string)) {
    navigateToLogin()
  }
}

const redirectToLoginIfInvalidURLOrUserIsNotLoggedIn = async () => {
  const currentRoute = navigator?.getCurrentRoute()
  if (isUserLoggedIn) {
    await accountStore.getProfile(localStorage.getItem('userId') as string)
    setupSignalRWorker()
  }
  if (!currentRoute?.name || currentRoute?.name === RESET_PASSWORD_URL_NAME) {
    return
  }
  if (!isUserLoggedIn && currentRoute?.name !== LOGIN_URL_NAME) {
    navigateToLogin()
  } else {
    validateLoginAndURLValidityAndPerformSetup()
  }
}

const handleSignalRConnectionStatus = (status: NotificationType, message: string) => {
  logInfo(message)
}

const setupSignalRWorker = () => {
  const worker = new signalRworker()
  worker.postMessage({
    name: MainToWorkerMessageType.Connect,
    accessToken: globalStore.accessToken
  })

  worker.onmessage = (event) => {
    const { name, ...rest } = event.data
    switch (name) {
      case WorkerToMainMessageType.Connected: {
        workersStore.saveWorker(WORKER_SIGNAL_R, worker)
        handleSignalRConnectionStatus(rest.status, rest.message)
        break
      }
      default:
        break
    }
  }
}

const subscribeToJobActivityUpdates = async () => {
  if (!signalRWorker.value) return
  realtimeStore.subscribeToJobActivityUpdates(signalRWorker.value, navigator)
}

onErrorCaptured((error) => {
  console.error('Error:', error)
})

onBeforeMount(() => {
  redirectToLoginIfInvalidURLOrUserIsNotLoggedIn()
})

watch(
  () => [currentRouteName.value, signalRWorker.value],
  async () => {
    if (!isUserLoggedIn) return
    await subscribeToJobActivityUpdates()
    if (currentRouteName.value == HAULAGE_JOBS_LIST_URL_NAME) {
      realtimeStore.unsubscribeToDigestateJobUpdates(signalRWorker.value, navigator)
      realtimeStore.unsubscribeToSpreadingJobUpdates(signalRWorker.value, navigator)
      realtimeStore.subscribeToHaulageJobUpdates(signalRWorker.value, navigator)
    }
    if (currentRouteName.value == DIGESTATE_JOBS_LIST_URL_NAME) {
      realtimeStore.unsubscribeToHaulageJobUpdates(signalRWorker.value, navigator)
      realtimeStore.unsubscribeToSpreadingJobUpdates(signalRWorker.value, navigator)
      realtimeStore.subscribeToDigestateJobUpdates(signalRWorker.value, navigator)
    }
    if (currentRouteName.value == SPREADING_JOBS_LIST_URL_NAME) {
      realtimeStore.unsubscribeToDigestateJobUpdates(signalRWorker.value, navigator)
      realtimeStore.unsubscribeToHaulageJobUpdates(signalRWorker.value, navigator)
      realtimeStore.subscribeToSpreadingJobUpdates(signalRWorker.value, navigator)
    }
    if (
      currentRouteName.value == SPREADING_JOBS_DETAILS_URL_NAME ||
      currentRouteName.value == SPREADING_JOBS_EDIT_JOB_URL_NAME
    ) {
      realtimeStore.unsubscribeToSpreadingJobUpdates(signalRWorker.value, navigator)
      realtimeStore.subscribeToSpreadingJobDetailsUpdates(signalRWorker.value, navigator)
    }
    if (
      currentRouteName.value == DIGESTATE_JOBS_DETAILS_URL_NAME ||
      currentRouteName.value == DIGESTATE_JOBS_EDIT_JOB_URL_NAME
    ) {
      realtimeStore.unsubscribeToDigestateJobUpdates(signalRWorker.value, navigator)
      realtimeStore.subscribeToDigestateJobDetailsUpdates(signalRWorker.value, navigator)
    }
    if (
      currentRouteName.value == HAULAGE_JOBS_DETAILS_URL_NAME ||
      currentRouteName.value == HAULAGE_JOBS_EDIT_JOB_URL_NAME
    ) {
      realtimeStore.unsubscribeToHaulageJobUpdates(signalRWorker.value, navigator)
      realtimeStore.subscribeToHaulageJobDetailsUpdates(signalRWorker.value, navigator)
    }
  }
)

watch(() => globalStore.isUserLoggedIn, redirectToLoginIfInvalidURLOrUserIsNotLoggedIn, {
  flush: 'post'
})
</script>

<template>
  <v-app :logged-in="globalStore.isUserLoggedIn">
    <NavBar v-if="userLoggedInAndNavigated" v-model="isNavbarCollapsed" />
    <AppBar v-if="userLoggedInAndNavigated" :navbar-collapsed="isNavbarCollapsed" />
    <v-main
      :navbar-collapsed="isNavbarCollapsed"
      :is-nested-path="globalStore.hasBreadcrumbs"
      :class="`main-container d-flex flex-column ${globalStore.isUserLoggedIn ? 'main-container--padding' : ''}`"
    >
      <router-view #default="{ Component }">
        <TopOfPageStickySection v-if="globalStore.isUserLoggedIn" />
        <component :is="Component" />
      </router-view>
    </v-main>
    <ErrorDialogBox />
    <Notifications />
    <ConfigFlyout />
    <AppBreadcrumbs />
    <ToastNotifications />
  </v-app>
</template>

<style scoped lang="scss">
header {
  line-height: 1.5;
  max-height: 100vh;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
  margin-top: 2rem;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
  border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
  border: 0;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }

  nav {
    text-align: left;
    margin-left: -1rem;
    font-size: 1rem;

    padding: 1rem 0;
    margin-top: 1rem;
  }
}
</style>
